import { debounce, deepClone, num_between } from '@shared/utils';
import { nextTick, reactive, watch, WatchStopHandle } from 'vue';
import { Emittable } from '@shared/utils/emittable';

type BidirectionalIteratorOptions = {
    startIndex?: number;
    loop?: boolean;
};

/** Bidirectional Iterator **/
export class BidiIterator {
    index: number = 0;
    start_index: number = 0;
    loop: boolean = false;

    data: any[] = [];

    /**
     * @param data - arrays for now
     * @param options.startIndex - A negative value set the index from the end of the collection by assigned value
     * @param options.loop - on done, sets index on the opposite end of iterable
     */
    constructor(data: any[] = [], { startIndex = -1, loop = false }: BidirectionalIteratorOptions = {}) {
        this.start_index = startIndex;
        this.loop = loop;

        this.setData(data);
    }

    // for..of iterates from current index until this.next().done returns true
    [Symbol.iterator]() {
        return this;
    }

    next() {
        let done = this.index >= this.len || ++this.index >= this.len;

        if (done && this.loop) this.setIndex(-1);

        return {
            index: this.index,
            value: this.data[done ? this.lastIndex : this.index],
            done,
        };
    }

    prev() {
        let done = this.index < 0 || --this.index < 0;

        if (done && this.loop) this.setIndex(-1, true);

        return {
            index: this.index,
            value: this.data[done ? 0 : this.index],
            done,
        };
    }

    // get entries without iteration
    get entry() {
        let index = num_between(this.index, 0, this.lastIndex);

        return {
            index,
            value: this.data[index],
            done: this.index < 0 || this.index > this.lastIndex,
        };
    }

    get next_entry() {
        let index = num_between(this.index + 1, 1, this.lastIndex);

        return {
            index,
            value: this.data[this.loop ? 0 : index],
            done: this.index + 1 > this.lastIndex,
        };
    }

    get prev_entry() {
        let index = num_between(this.index - 1, 0, this.lastIndex - 1);

        return {
            index,
            value: this.data[this.loop ? this.lastIndex : index],
            done: this.index - 1 < 0,
        };
    }

    /** flip arg counts index from the end of the dataset */
    entryAtIndex(idx: number, flip: boolean = false) {
        let index = num_between(flip ? this.len - idx : idx, 0, this.lastIndex);

        return {
            index,
            value: this.data[index],
            done: index - 1 < 0 || index + 1 > this.lastIndex,
        };
    }

    setData(data: any) {
        this.data = data;

        this.resetIndex();
    }

    setIndex(idx: number = 0, flip: boolean = false) {
        this.index = flip ? this.lastIndex - idx : idx;

        return this.entry;
    }

    resetIndex() {
        return this.setIndex(this.start_index - 1, this.start_index < 0);
    }

    /** truthy spread arg will concat an array of items */
    add(items: any | any[], idx: number = this.len, spread: boolean = false) {
        if (spread && Array.isArray(items)) this.data.splice(idx, 0, ...items);
        else this.data.splice(idx, 0, items);

        if (this.index >= idx - 1) this.setIndex(-1, true);
    }

    /** negative index targets the last entry */
    del(idx: number, deleteCount: number = this.len - idx) {
        idx = idx === -1 ? this.lastIndex : idx;

        this.data.splice(idx, deleteCount);

        if (this.index >= this.lastIndex) this.setIndex(-1, true);
    }

    get len(): number {
        return this.data.length;
    }

    get lastIndex(): number {
        return this.data.length - 1;
    }
}

type HistorySetupOptions = {
    ignore?: (string | number)[];
    fullProps?: (string | number)[];
    events?: Emittable;
    sizeCap?: number;
};

type HistorySetupArgs<T> = [o: T, options?: HistorySetupOptions];

type HistoryStepEntry = {
    type: keyof HistoryStep & string;
    key: string;
    path: (string | number)[];
    value: any;
    prevValue: any;
};

type HistoryStep = {
    [k in 'add' | 'del' | 'update']: Map<(string | number)[], HistoryStepEntry[]>;
};

/** Object mutation comparer & history managment */
export class HistoryClass<T extends object = any> {
    bidi: BidiIterator;
    sizeCap = 100;

    ignore: (string | number)[] = [];
    ignoreRegExp: RegExp | null = null;
    fullProps: (string | number)[] = [];
    fullPropRegExp: RegExp | null = null;

    source: T | Func = <T | Func>{};
    cache: T = <T>{};

    timeout: Timeout = <any>0;

    //** CONSTRUCTOR */
    constructor(...args: HistorySetupArgs<T>) {
        this.bidi = new BidiIterator();
        this.setup.apply(this, args);
    }

    setup(source: T | Func, { ignore, fullProps, events, sizeCap }: HistorySetupOptions = {}, watchOptions?: any) {
        this.source = source;
        this.cache = deepClone(typeof source === 'function' ? (<Func>source)() : source);
        this.watch(source, watchOptions);

        this.ignore = ignore || [];
        this.fullProps = fullProps || [];

        if (this.ignore.length) this.ignoreRegExp = new RegExp(`^(${this.ignore.join('|')})$`);
        if (this.fullProps.length) this.fullPropRegExp = new RegExp(`^(${this.fullProps.join('|')})$`);

        if (sizeCap) this.sizeCap = sizeCap;
    }

    reset() {
        this.bidi.setData([]);
    }

    watcher: PausableWatcher | null = null;
    watch(source: T | Func, options?: any) {
        if (this.bidi.len === 0) this.bidi.add({ add: new Map(), del: new Map(), update: new Map() });

        this.watcher = new PausableWatcher(
            source,
            (o: T, old: T, methods) => {
                // group entries by the path[] property and use as Map key
                let changes: HistoryStep = { add: new Map(), del: new Map(), update: new Map() };
                const compare = (o1: any, o2: any, key?: string, path: (string | number)[] = [], changeProp?: HistoryStepEntry) => {
                    let changed = false,
                        changeTarget = !!changeProp;

                    // when compare is done, the remaining keys
                    // in [ o1_keys ] are properties that've been deleted
                    let o1_keys: any = {};
                    for (let k in o1) o1_keys[k] = o1[k];

                    for (let k in o2) {
                        let fullprop: any;
                        delete o1_keys[k];

                        if (this.ignoreRegExp?.test(k)) continue;

                        // update the whole property if there are nested changes
                        if (this.fullPropRegExp?.test(k))
                            fullprop = {
                                type: 'update',
                                key: k,
                                path,
                                value: o2[k],
                                prevValue: o1[k],
                            };

                        // recursive traverse
                        if (o1[k] && o2[k] && typeof o2[k] === 'object') {
                            let nestedChanges = compare(o1[k], o2[k], k, [...path, k], fullprop);

                            if (nestedChanges && (!!fullprop || !!changeProp)) changeTarget = changed = true;
                        } else if (o1[k] === undefined) {
                            // added properties
                            changed = true;

                            if (changeTarget) break;

                            this.#addStepEntry(changes, {
                                type: 'add',
                                key: k,
                                path,
                                value: deepClone(o2[k]),
                                prevValue: undefined,
                            });
                        } else if (o1[k] !== o2[k]) {
                            // plain value compare
                            changed = true;

                            if (changeTarget) break;

                            this.#addStepEntry(changes, {
                                type: 'update',
                                key: k,
                                path,
                                value: deepClone(o2[k]),
                                prevValue: o1[k],
                            });
                        }
                    }

                    for (let k in o1_keys) {
                        // deleted properties
                        changed = true;

                        if (changeTarget) break;

                        this.#addStepEntry(changes, {
                            type: 'del',
                            key: k,
                            path,
                            value: undefined,
                            prevValue: o1[k],
                        });
                    }

                    if (changed) {
                        if (changeTarget && changeProp?.path === path) this.#addStepEntry(changes, deepClone(changeProp));

                        return true;
                    } else return false;
                };

                compare(this.cache, o);

                if (changes.add.size === 0 && changes.del.size === 0 && changes.update.size === 0) {
                    return;
                } else console.log('::: found:', changes, this.bidi.index);

                if (this.bidi.index < this.bidi.lastIndex) this.bidi.del(this.bidi.index < 0 ? (this.bidi.index = 1) : ++this.bidi.index);

                if (this.bidi.len >= this.sizeCap) this.bidi.del(0, this.bidi.len);

                this.bidi.add(changes);

                this.cache = deepClone<T>(o);
            },
            { debounce: { active: true } }
        );
    }

    dispose() {
        if (this.watcher) {
            this.watcher.dispose();
            this.watcher = null;
        }

        this.reset();
    }

    /** get property by path */
    #getProp(path: (string | number)[], o: T & { [k: string | number]: any } = typeof this.source === 'function' ? (<Func>this.source)() : this.source || {}) {
        for (let k of path) o = o[k];

        return o;
    }

    #addStepEntry(o: HistoryStep, entry: HistoryStepEntry) {
        let { type, path } = entry,
            map = o[type];

        if (map.get(path)?.push(entry) === undefined) map.set(path, [entry]);
    }

    next() {
        if (!this.bidi.len) return;

        let step = this.bidi.next();

        if (step.done) return;

        //** skip the first index */
        if (step.index <= 0) step = this.bidi.setIndex(1);

        let { add, del, update } = step.value, // prevValue -> value
            newLines = [];

        // add
        for (let [path, entries] of add) {
            let o = this.#getProp(path);

            for (let { key, value } of entries) {
                o[key] = value;

                if (value.fr || value.to) newLines.push(o[key]);
            }
        }

        // delete
        for (let [path, entries] of del) {
            let o = this.#getProp(path);

            for (let { key } of entries) delete o[key];
        }

        // update
        for (let [path, entries] of update) {
            let o = this.#getProp(path);

            for (let { key, value } of entries)
                if (Array.isArray(o[key])) {
                    o[key].splice(0, o[key].length);

                    for (let v of value) // ko reactivity breaks when using splice to add multiple entries
                        o[key].push(v);
                } else o[key] = value;
        }

        this.cache = deepClone(typeof this.source === 'function' ? (<Func>this.source)() : this.source);

        this.watcher?.muteUpdate();

        return {
            newLines,
            added: add.length,
            deleted: del.length,
            updated: update.length,
        };
    }

    prev() {
        if (!this.bidi.len) return;

        let step = this.bidi.prev();

        if (step.done || !this.bidi.next_entry.value) return;

        //** skip the last index */
        if (step.index >= this.bidi.lastIndex) step = this.bidi.setIndex(1, true);

        let { add, del, update } = this.bidi.next_entry.value, // value -> prevValue
            newLines = [];

        // delete
        for (let [path, entries] of add) {
            let o = this.#getProp(path);

            for (let { key } of entries) delete o[key];
        }

        // add
        for (let [path, entries] of del) {
            let o = this.#getProp(path);

            for (let { key, prevValue } of entries) {
                o[key] = prevValue;

                if (prevValue.fr || prevValue.to) newLines.push(o[key]);
            }
        }

        // update
        for (let [path, entries] of update) {
            let o = this.#getProp(path);

            for (let { key, prevValue } of entries)
                if (Array.isArray(o[key])) {
                    o[key].splice(0, o[key].length);

                    for (let v of prevValue) // ko reactivity breaks when using splice to add multiple entries
                        o[key].push(v);
                } else o[key] = prevValue;
        }

        this.cache = deepClone(typeof this.source === 'function' ? (<Func>this.source)() : this.source);
        this.watcher?.muteUpdate();

        return {
            newLines,
            added: add.length,
            deleted: del.length,
            updated: update.length,
        };
    }
}

interface PausableWatcherOptions {
    eager?: boolean;
    debounce?: {
        active?: boolean;
        channels?: number[];
    };
}

class PausableWatcher<T extends object = any> {
    watcherState = reactive({
        active: true,
    });

    watchStopHandle: WatchStopHandle | null = null;
    idle_count = 0;
    options = {
        eager: false,
        debounce: {
            active: true,
            channels: [0],
        },
    };

    constructor(source: T | Func, callback: Func, options?: PausableWatcherOptions) {
        this.setupWatcher(source, callback, options);
    }

    setupWatcher(source: T | Func, callback: Func, options?: PausableWatcherOptions) {
        if (options) {
            this.options.eager = options.eager ?? this.options.eager;

            Object.assign(this.options.debounce, options.debounce);
        }

        this.watcherState.active = this.options.eager;

        this.dispose();

        this.watchStopHandle = watch(typeof source === 'function' ? (<Func>source)() : source, (o: T, old: T) => {
            // console.log('watcher triggered - ignore next update:', this.watcherState.active)

            if (this.watcherState.active) {
                if (this.options.debounce.active)
                    debounce(() => {
                        callback(o, old, {
                            active: this.state.active,
                            pause: this.pause,
                            resume: this.resume,
                            dispose: this.dispose,
                            muteUpdate: this.muteUpdate,
                        });
                    });
                else
                    callback(o, old, { active: this.state.active, pause: this.pause, resume: this.resume, dispose: this.dispose, muteUpdate: this.muteUpdate });
            } else this.idle_count++;
        });

        if (!this.watcherState.active) this.resume();
    }

    get state() {
        return {
            active: this.watcherState.active,
        };
    }

    async muteUpdate() {
        this.pause();

        await nextTick();

        if (this.options.debounce.active) for (let channel of this.options.debounce.channels) debounce.clear(channel);

        this.resume();
    }

    pause() {
        this.watcherState.active = false;
    }

    resume() {
        this.watcherState.active = true;
    }

    dispose() {
        if (this.watchStopHandle) {
            this.watchStopHandle();
            this.watchStopHandle = null;
        }
    }
}
